import { useState, useRef, useEffect } from "react";
import axios, { API } from "../../api";

/**
 * Public Aura demo chat — embedded in the About page.
 * Stateless on the server: caller passes full message history each request.
 */
export function PublicAuraChat() {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hey — I'm Aura. Ask me anything 💜" },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [rateLimited, setRateLimited] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    const newMessages = [...messages, { role: "user", content: text }];
    setMessages(newMessages);
    setBusy(true);
    try {
      const res = await axios.post(`${API}/public/aura-demo`, {
        messages: newMessages,
      });
      setMessages((m) => [...m, { role: "assistant", content: res.data.response }]);
      if (res.data.messages_remaining_this_hour <= 0) {
        setRateLimited(true);
      }
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 429) {
        setRateLimited(true);
        setMessages((m) => [...m, { role: "assistant", content: detail || "Let's pick this up properly — download me at aura.wwwwhynot.com 💜" }]);
      } else {
        setMessages((m) => [...m, { role: "assistant", content: "I drifted off for a second. Try again?" }]);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="public-aura-chat" data-testid="public-aura-chat">
      <div className="public-chat-window" ref={scrollRef}>
        {messages.map((m, i) => (
          <div
            key={i}
            className={`public-chat-bubble ${m.role === "user" ? "user" : "aura"}`}
            data-testid={`public-chat-msg-${i}`}
          >
            {m.content}
          </div>
        ))}
        {busy && (
          <div className="public-chat-bubble aura typing">
            <span></span><span></span><span></span>
          </div>
        )}
      </div>
      <div className="public-chat-composer">
        <input
          type="text"
          className="public-chat-input"
          placeholder={rateLimited ? "Download Aura to keep chatting 💜" : "Ask Aura anything..."}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") send(); }}
          disabled={busy || rateLimited}
          maxLength={500}
          data-testid="public-chat-input"
        />
        <button
          className="public-chat-send"
          onClick={send}
          disabled={busy || rateLimited || !input.trim()}
          data-testid="public-chat-send"
        >
          ✦
        </button>
      </div>
    </div>
  );
}
